import React, { useMemo } from 'react';
import {
  Lock, CheckCircle, AlertTriangle, Percent,
  RefreshCw, ShieldAlert,
} from 'lucide-react';
import { useRetentionStats } from '../hooks/useRetentionStats';
import { useAuth } from '../hooks/useAuth';
import { useSafeMode } from '../contexts/SafeModeContext';
import { useDashboardFilter } from '../contexts/useDashboardFilterHooks';
import { formatCompactCurrency } from '../lib/calculations';
import {
  RetentionOverviewDonut,
  RetentionRecoveryGauge,
  RetentionByProjectChart,
  RetentionMonthlyTrendChart,
  RetentionAgingChart,
  ProjectRetentionTrendChart,
  RetentionChartAnimationStyles,
} from './RetentionDashboardCharts';
import './PnlDashboardCharts.css';
import { HowWorkedOut } from './howWorkedOut/HowWorkedOut';
import type { MetricDefinition, RightNowValue } from './howWorkedOut/metricDefinition';
import { RETENTION_FIGURES } from '../hooks/useRetentionStats.metrics';

/* ═══════════════════════════════════════════════════════════════════════════
   Animation keyframes for metric cards
   ═══════════════════════════════════════════════════════════════════════════ */

const metricAnimStyles = `
@keyframes retMetricSlideIn {
  from { opacity: 0; transform: translateY(12px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
.ret-metric-card {
  animation: retMetricSlideIn 0.4s ease-out both;
}
.ret-metric-card:nth-child(1) { animation-delay: 0s; }
.ret-metric-card:nth-child(2) { animation-delay: 0.06s; }
.ret-metric-card:nth-child(3) { animation-delay: 0.12s; }
.ret-metric-card:nth-child(4) { animation-delay: 0.18s; }
`;

/* ═══════════════════════════════════════════════════════════════════════════
   Skeleton Loader
   ═══════════════════════════════════════════════════════════════════════════ */

function RetentionSkeleton() {
  const shimmer: React.CSSProperties = {
    background: 'linear-gradient(90deg, var(--bg-tertiary, #f3f4f6) 25%, var(--bg-secondary, #e5e7eb) 50%, var(--bg-tertiary, #f3f4f6) 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    borderRadius: 12,
  };
  return (
    <div>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[1, 2, 3, 4].map(i => <div key={i} style={{ ...shimmer, height: 100 }} />)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 20, marginBottom: 24 }}>
        <div style={{ ...shimmer, height: 320 }} />
        <div style={{ ...shimmer, height: 320 }} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Metric Card (reuses PnlDashboardSection pattern)
   ═══════════════════════════════════════════════════════════════════════════ */

interface MetricCardProps {
  label: string; value: string; subLabel?: string;
  icon: React.ReactNode; color: string; bgGradient: string;
  /** SEQ-240: the "How is this worked out" pop-up, replacing the old hover title. */
  definition: MetricDefinition;
  rightNow?: readonly RightNowValue[];
}

function RetMetricCard({ label, value, subLabel, icon, color, bgGradient, definition, rightNow }: MetricCardProps) {
  return (
    <div
      className="card ret-metric-card"
      style={{
        padding: '1.25rem', background: bgGradient,
        border: `1px solid ${color}20`, borderRadius: 16,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
      onMouseEnter={(e) => { (e.currentTarget).style.transform = 'translateY(-2px)'; (e.currentTarget).style.boxShadow = `0 8px 24px ${color}15`; }}
      onMouseLeave={(e) => { (e.currentTarget).style.transform = 'translateY(0)'; (e.currentTarget).style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span className="hwo-heading">
          <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</span>
          <HowWorkedOut definition={definition} rightNow={rightNow} />
        </span>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>{icon}</div>
      </div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-primary, #111827)', lineHeight: 1.2 }}>{value}</div>
      {subLabel && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #9ca3af)', marginTop: 6 }}>{subLabel}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Main Section
   ═══════════════════════════════════════════════════════════════════════════ */

export function RetentionDashboardSection() {
  const { profile, loading: authLoading } = useAuth();
  const hasFinancialAccess = Boolean(profile?.can_view_financials);
  const { isSafeMode } = useSafeMode();
  const stats = useRetentionStats();
  const { effectiveProjectIds, isFiltered } = useDashboardFilter();

  // ── Client-side filtering of retention data ──────────────────────────
  const filtered = useMemo(() => {
    if (!isFiltered || !effectiveProjectIds) return stats;

    const idSet = new Set(effectiveProjectIds);

    const perProjectRetention = stats.perProjectRetention.filter(p => idSet.has(p.projectId));
    const projectMonthlyRetention = stats.projectMonthlyRetention.filter(p => idSet.has(p.projectId));

    // Recalculate aggregates
    const totalHeld = perProjectRetention.reduce((s, p) => s + p.totalHeld, 0);
    const totalReceived = perProjectRetention.reduce((s, p) => s + p.totalReceived, 0);
    const totalOutstanding = totalHeld - totalReceived;
    const recoveryPercent = totalHeld > 0 ? (totalReceived / totalHeld) * 100 : 0;
    const projectCount = perProjectRetention.length;

    return {
      ...stats,
      perProjectRetention,
      projectMonthlyRetention,
      totalHeld,
      totalReceived,
      totalOutstanding,
      recoveryPercent,
      projectCount,
    };
  }, [stats, effectiveProjectIds, isFiltered]);

  // Auth gate + Safe Mode
  if (authLoading) return null;
  if (!hasFinancialAccess || isSafeMode) return null;

  // Error state
  if (stats.error && !stats.loading) {
    return (
      <div style={{ marginBottom: 'var(--spacing-xl, 2rem)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary, #111827)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldAlert size={20} style={{ color: '#f59e0b' }} />
            Retention Analytics
          </h3>
        </div>
        <div className="card" style={{ padding: '2rem', textAlign: 'center', borderRadius: 16 }}>
          <AlertTriangle size={32} style={{ color: '#f59e0b', marginBottom: 12 }} />
          <p style={{ color: 'var(--text-secondary, #6b7280)', marginBottom: 12 }}>{stats.error}</p>
          <button onClick={stats.refresh} className="btn btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 'var(--spacing-xl, 2rem)' }}>
      <style>{metricAnimStyles}</style>
      <RetentionChartAnimationStyles />

      {/* Section Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary, #111827)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShieldAlert size={20} style={{ color: '#f59e0b' }} />
          Retention Analytics
          {isFiltered && (
            <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-muted, #9ca3af)', marginLeft: 4 }}>
              ({filtered.projectCount} projects)
            </span>
          )}
        </h3>
        {!stats.loading && (
          <button onClick={stats.refresh} className="btn btn-ghost btn-sm" style={{ color: 'var(--text-muted, #9ca3af)', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}>
            <RefreshCw size={14} /> Refresh
          </button>
        )}
      </div>

      {stats.loading ? <RetentionSkeleton /> : (
        <>
          {/* ────────── Row 1: Metric Cards ────────── */}
          <div className="pnl-grid-row pnl-grid-metrics">
            <RetMetricCard
              label="Retention held"
              value={formatCompactCurrency(filtered.totalHeld)}
              subLabel={`${filtered.projectCount} projects`}
              icon={<Lock size={18} />}
              color="#f59e0b"
              bgGradient="linear-gradient(135deg, rgba(245,158,11,0.04) 0%, rgba(245,158,11,0.01) 100%)"
              definition={RETENTION_FIGURES["retention.held"]}
              rightNow={[
                { label: 'Retention held', value: filtered.totalHeld, unit: 'currency' },
                { label: 'Projects', value: filtered.projectCount, unit: 'count' },
              ]}
            />
            <RetMetricCard
              label="Received"
              value={formatCompactCurrency(filtered.totalReceived)}
              subLabel="Released back"
              icon={<CheckCircle size={18} />}
              color="#10b981"
              bgGradient="linear-gradient(135deg, rgba(16,185,129,0.04) 0%, rgba(16,185,129,0.01) 100%)"
              definition={RETENTION_FIGURES["retention.received"]}
              rightNow={[{ label: 'Received', value: filtered.totalReceived, unit: 'currency' }]}
            />
            <RetMetricCard
              label="Outstanding"
              value={formatCompactCurrency(filtered.totalOutstanding)}
              subLabel="Still held by clients"
              icon={<AlertTriangle size={18} />}
              color="#ef4444"
              bgGradient="linear-gradient(135deg, rgba(239,68,68,0.04) 0%, rgba(239,68,68,0.01) 100%)"
              definition={RETENTION_FIGURES["retention.outstanding"]}
              rightNow={[
                { label: 'Held', value: filtered.totalHeld, unit: 'currency' },
                { label: 'Received', value: filtered.totalReceived, unit: 'currency' },
                { label: 'Outstanding', value: filtered.totalOutstanding, unit: 'currency' },
              ]}
            />
            <RetMetricCard
              label="Recovery Rate"
              value={`${filtered.recoveryPercent.toFixed(1)}%`}
              subLabel={filtered.recoveryPercent === 0 ? 'No retention recovered yet' : 'Of total held'}
              icon={<Percent size={18} />}
              color={filtered.recoveryPercent >= 70 ? '#10b981' : filtered.recoveryPercent >= 30 ? '#f59e0b' : '#ef4444'}
              bgGradient={
                filtered.recoveryPercent >= 70
                  ? 'linear-gradient(135deg, rgba(16,185,129,0.04) 0%, rgba(16,185,129,0.01) 100%)'
                  : filtered.recoveryPercent >= 30
                    ? 'linear-gradient(135deg, rgba(245,158,11,0.04) 0%, rgba(245,158,11,0.01) 100%)'
                    : 'linear-gradient(135deg, rgba(239,68,68,0.04) 0%, rgba(239,68,68,0.01) 100%)'
              }
              definition={RETENTION_FIGURES["retention.recovery-rate"]}
              rightNow={[
                { label: 'Received', value: filtered.totalReceived, unit: 'currency' },
                { label: 'Held', value: filtered.totalHeld, unit: 'currency' },
                { label: 'Recovery rate', value: filtered.recoveryPercent, unit: 'percent' },
              ]}
            />
          </div>

          {/* ────────── Row 2: Donut + Recovery Gauge ────────── */}
          <div className="pnl-grid-row pnl-grid-2col">
            <RetentionOverviewDonut
              totalHeld={filtered.totalHeld}
              totalReceived={filtered.totalReceived}
              totalOutstanding={filtered.totalOutstanding}
            />
            <RetentionRecoveryGauge recoveryPercent={filtered.recoveryPercent} />
          </div>

          {/* ────────── Row 3: By Project + Monthly Trend ────────── */}
          <div className="pnl-grid-row pnl-grid-2col">
            <RetentionByProjectChart data={filtered.perProjectRetention} />
            <RetentionMonthlyTrendChart data={filtered.monthlyTrend} />
          </div>

          {/* ────────── Row 4: Aging + Per-Project Trend ────────── */}
          <div className="pnl-grid-row pnl-grid-2col">
            <RetentionAgingChart data={filtered.agingBuckets} />
            {filtered.projectMonthlyRetention.length > 0 && (
              <ProjectRetentionTrendChart data={filtered.projectMonthlyRetention} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
